import { computed, effect, EffectRef, Injector, Signal, untracked } from '@angular/core';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faArrowPointer } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoService } from '@jsverse/transloco';
import { Map } from 'maplibre-gl';

import { AbstractMapControl } from '@connect-services/client/components/map/abstract-map-control';
import { ViewParam } from '@connect-services/client/components/map/map.component';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';

export class SelectionModalControl extends AbstractMapControl {
    private readonly activeEffectRef: EffectRef;
    private readonly viewParam: Signal<ViewParam> = computed(() => {
        if (this.mapEditModeService.isMapSelectionControlDisabled()) {
            return {
                icon: faArrowPointer,
                labelTranslation: 'editMode.cannotSelectDuringPointEdition',
            };
        }

        return this.selectionService.isSelectionActive()
            ? {
                  icon: faArrowPointer,
                  labelTranslation: 'close',
              }
            : {
                  icon: faArrowPointer,
                  labelTranslation: 'select',
              };
    });

    public constructor(
        map: Map,
        private readonly mapEditModeService: MapEditModeService,
        private readonly mapModalService: MapModalService,
        private readonly selectionService: SelectionService,
        private readonly translocoService: TranslocoService,
        injector: Injector,
    ) {
        super();
        this.map = map;

        this.activeEffectRef = effect(
            () => {
                const isDisabled = this.mapEditModeService.isMapSelectionControlDisabled();
                const isSelectionActive = this.selectionService.isSelectionActive();

                untracked(() => {
                    this.refreshButton();

                    if (isDisabled && isSelectionActive) {
                        void this.closeModal();
                    }
                });
            },
            { injector },
        );
    }

    protected initButton(): void {
        this.setupButton('selection-modal-control', () => this.toggleModal(), ['selection-modal-control']);
        this.refreshButton();
    }

    protected override setButtonIcon(): void {
        this.button.innerHTML = icon(this.viewParam().icon).html[0];
    }

    private async closeModal(): Promise<void> {
        this.selectionService.isSelectionActive.set(false);
        this.refreshButton();

        await this.selectionService.clearAllSelections();

        this.mapModalService.close(MapModal.SELECTION);
    }

    private refreshButton(): void {
        this.setButtonIcon();

        this.setButtonState({
            isActive: this.selectionService.isSelectionActive(),
            isDisabled: this.mapEditModeService.isMapSelectionControlDisabled(),
        });

        this.setButtonTitle(this.translocoService.translate(this.viewParam().labelTranslation));
    }

    private async toggleModal(): Promise<void> {
        if (!this.map || this.mapEditModeService.isMapSelectionControlDisabled()) {
            return;
        }

        this.selectionService.isSelectionActive.set(!this.selectionService.isSelectionActive());
        this.refreshButton();

        if (!this.selectionService.isSelectionActive()) {
            await this.selectionService.clearAllSelections();

            this.mapModalService.close(MapModal.SELECTION);

            return;
        }

        this.mapModalService.open(MapModal.SELECTION);
    }
}
